<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  placement: {
    type: String,
    default: 'bottom-start',
    validator: (value) => ['bottom-start', 'bottom-end', 'top-start', 'top-end'].includes(value)
  },
  offset: {
    type: Number,
    default: 8
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true
  },
  trigger: {
    type: String,
    default: 'click',
    validator: (value) => ['click', 'hover'].includes(value)
  },
  hoverDelay: {
    type: Number,
    default: 150
  },
  block: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  dropdownStyle: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue']);

const dropdown = ref(null);
const trigger = ref(null);
let hoverTimeout = null;

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const dropdownClasses = computed(() => [
  'ui-dropdown',
  `ui-dropdown--${props.placement}`,
  {
    'ui-dropdown--open': isOpen.value,
    'ui-dropdown--compact': props.compact
  }
]);

const containerClasses = computed(() => [
  'ui-dropdown-container',
  {
    'ui-dropdown-container--block': props.block
  }
]);

const handleClickOutside = (event) => {
  if (
    props.closeOnClickOutside &&
    isOpen.value &&
    dropdown.value &&
    !dropdown.value.contains(event.target) &&
    trigger.value &&
    !trigger.value.contains(event.target)
  ) {
    isOpen.value = false;
  }
};

const handleEscape = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false;
  }
};

const handleMouseEnter = () => {
  if (props.trigger === 'hover') {
    clearTimeout(hoverTimeout);
    isOpen.value = true;
  }
};

const handleMouseLeave = () => {
  if (props.trigger === 'hover') {
    hoverTimeout = setTimeout(() => {
      isOpen.value = false;
    }, props.hoverDelay);
  }
};

const handleClick = (event) => {
  if (props.trigger === 'click') {
    // Stop propagation when used inside other clickable elements
    if (event) {
      event.stopPropagation();
    }
    isOpen.value = !isOpen.value;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleEscape);
});
</script>

<template>
  <div :class="containerClasses" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <!-- Trigger slot -->
    <div ref="trigger" @click="handleClick">
      <slot name="trigger" :isOpen="isOpen" />
    </div>

    <!-- Dropdown content -->
    <Transition name="dropdown">
      <div
        v-if="isOpen"
        ref="dropdown"
        :class="dropdownClasses"
        role="menu"
        :style="{
          '--offset': `${offset}px`,
          ...dropdownStyle
        }"
      >
        <div class="ui-dropdown__content">
          <slot :close="() => (isOpen = false)" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.ui-dropdown-container {
  position: relative;
  display: inline-block;
}

.ui-dropdown-container--block {
  display: block;
  width: 100%;
}

.ui-dropdown-container--block .ui-dropdown--bottom-start {
  left: 0;
}

.ui-dropdown {
  position: absolute;
  z-index: 1000;
  min-width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.2s ease-in-out;
}

.ui-dropdown--open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.ui-dropdown--bottom-start {
  top: calc(100% + var(--offset));
  left: 0;
}

.ui-dropdown--bottom-end {
  top: calc(100% + var(--offset));
  right: 0;
}

.ui-dropdown--top-start {
  bottom: calc(100% + var(--offset));
  left: 0;
  transform-origin: bottom center;
}

.ui-dropdown--top-end {
  bottom: calc(100% + var(--offset));
  right: 0;
  transform-origin: bottom center;
}

.ui-dropdown__content {
  padding: 24px;
  max-height: 500px;
  overflow-y: auto;
}

.ui-dropdown--compact .ui-dropdown__content {
  padding: 8px 24px;
}

/* Transition styles */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease-in-out;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
